import type { Drill } from '../game/drills';
import { mwLabel } from '../game/scoring';
import type { DrillResult } from '../game/types';
import { must } from './dom';
import { str } from './strings';
import { rankName } from './tiers';

export interface YardRow {
  id: string;
  name: string;
  /** `null` until the drill has been completed. */
  score: DrillResult | null;
  /** This is the drill currently on the board. */
  current: boolean;
}

/** The yard in authored order: easiest first, within each family. */
export function buildYardRows(
  drills: readonly Drill[],
  done: Record<string, DrillResult>,
  currentId: string | null,
): YardRow[] {
  return drills.map((d) => ({
    id: d.id,
    name: str().drills[d.id].name,
    score: done[d.id] ?? null,
    current: d.id === currentId,
  }));
}

/** Render the drill list and hand back each row's id on click. */
export function renderYard(rows: YardRow[], onPick: (id: string) => void): void {
  const list = must('yardList');
  list.innerHTML = rows
    .map((row) => {
      const rank = row.score ? rankName(row.score.tier) : '';
      // MW stacks above the rank: a lesson-stating name runs longer than the
      // archive's day tag, so the archive's one-line score would fight it
      // for width on a phone (STYLEGUIDE §7).
      const right = row.score
        ? `<span class="score"><span class="mw-tier"><b>${mwLabel(row.score.mw)}</b> ${str().unitMW}</span>` +
          `${rank ? `<span class="rank">${rank}</span>` : ''}</span>`
        : `<span class="unplayed">${str().rowPlay}</span>`;
      // The row carries the name alone; the drill's lesson waits under the
      // map once the drill is on the board (STYLEGUIDE §7).
      return (
        `<button class="arch-row${row.current ? ' current' : ''}" data-drill="${row.id}"` +
        `${row.current ? ' aria-current="true"' : ''}>` +
        `<span class="drill-name"><b>${row.name}</b></span>${right}</button>`
      );
    })
    .join('');

  list.querySelectorAll<HTMLButtonElement>('.arch-row').forEach((b) => {
    b.addEventListener('click', () => onPick(String(b.dataset.drill)));
  });
}
